/**
 * Game Management Zustand Store
 * 
 * Provides centralized state management for game creation and payout structure building.
 * Designed to work with the PayoutStructureBuilder component and future game management features.
 * 
 * Features:
 * - Persistent game state across sessions
 * - Undo/redo functionality for payout structure changes
 * - Draft saving and restoration
 * - Form validation state management
 * - Import/export state management
 * - Toast notification state
 * 
 * @module TabPicker.GameStore
 */

import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

/**
 * Payout Row Definition
 * 
 * Represents a single payout tier in the game structure.
 */
export interface PayoutRow {
  /** Unique identifier for the row */
  id: string;
  /** Prize amount in dollars */
  amount: number;
  /** Number of tickets with this prize amount */
  count: number;
}

/**
 * Game Configuration
 * 
 * Complete game configuration including metadata and payout structure.
 */
export interface GameConfig {
  /** Game display name */
  dealName: string;
  /** Price per ticket as string for input handling */
  ticketPrice: string;
  /** Total number of tickets as string for input handling */
  totalTickets: string;
  /** Array of payout tiers */
  rows: PayoutRow[];
}

/**
 * Import/Export State
 * 
 * State for managing data import/export functionality.
 */
interface ImportExportState {
  /** Whether import dialog is visible */
  showImport: boolean;
  /** Raw import text from user */
  importText: string;
  /** Parsed import preview before confirmation */
  importPreview: PayoutRow[] | null;
}

/**
 * UI State
 * 
 * State for managing UI interactions and notifications.
 */
interface UIState {
  /** Current toast notification message */
  toast: string | null;
  /** Input values for form fields (for controlled inputs) */
  inputValues: Record<string, string>;
}

/**
 * History State
 * 
 * State for managing undo/redo functionality.
 */
interface HistoryState {
  /** Array of previous game configurations */
  history: GameConfig[];
  /** Current position in history */
  historyIndex: number;
  /** Maximum number of history entries to keep */
  maxHistorySize: number;
}

/**
 * Game Store State
 * 
 * Complete state shape for game management.
 */
interface GameState extends GameConfig, ImportExportState, UIState, HistoryState {
  /** Whether the current game has unsaved changes */
  hasUnsavedChanges: boolean;
  /** Timestamp of last save */
  lastSaved: number | null;
  /** Whether auto-save is enabled */
  autoSave: boolean;
}

/**
 * Game Store Actions
 * 
 * All actions available for game state management.
 */
interface GameActions {
  // Basic game configuration actions
  setDealName: (name: string) => void;
  setTicketPrice: (price: string) => void;
  setTotalTickets: (total: string) => void;
  setRows: (rows: PayoutRow[]) => void;
  
  // Payout row management
  addPayoutRow: (amount?: number) => void;
  updatePayoutRow: (id: string, updates: Partial<PayoutRow>) => void;
  removePayoutRow: (id: string) => void;
  duplicatePayoutRow: (id: string) => void;
  sortPayoutRows: () => void;
  
  // Import/export actions
  setShowImport: (show: boolean) => void;
  setImportText: (text: string) => void;
  setImportPreview: (preview: PayoutRow[] | null) => void;
  importRows: (rows: PayoutRow[]) => void;
  exportGameConfig: () => GameConfig;
  
  // UI actions
  setToast: (message: string | null) => void;
  setInputValue: (key: string, value: string) => void;
  clearInputValues: () => void;
  
  // History actions
  saveToHistory: () => void;
  undo: () => boolean;
  redo: () => boolean;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clearHistory: () => void;
  
  // Game lifecycle actions
  loadExampleGame: () => void;
  resetGame: () => void;
  saveGame: () => void;
  loadGame: (config: GameConfig) => void;
  
  // Utility actions
  markSaved: () => void;
  setAutoSave: (enabled: boolean) => void;
}

/**
 * Combined Game Store Type
 */
export type GameStore = GameState & GameActions;

/**
 * Generate unique ID for payout rows
 */
const uid = () => Math.random().toString(36).slice(2, 11);

/**
 * Default payout structure
 */
const getDefaultPayoutRows = (): PayoutRow[] => [
  { id: uid(), amount: 500, count: 0 },
  { id: uid(), amount: 250, count: 0 },
  { id: uid(), amount: 100, count: 0 },
  { id: uid(), amount: 50, count: 0 },
  { id: uid(), amount: 25, count: 0 },
  { id: uid(), amount: 20, count: 0 },
  { id: uid(), amount: 10, count: 0 },
  { id: uid(), amount: 5, count: 0 },
  { id: uid(), amount: 2, count: 0 },
  { id: uid(), amount: 1, count: 0 },
];

/**
 * Example game configuration
 */
const getExampleGame = (): Partial<GameConfig> => ({
  dealName: "Lucky 7s Pull Tab",
  ticketPrice: "1.00",
  totalTickets: "1000",
  rows: [
    { id: uid(), amount: 100, count: 1 },
    { id: uid(), amount: 50, count: 2 },
    { id: uid(), amount: 25, count: 5 },
    { id: uid(), amount: 10, count: 10 },
    { id: uid(), amount: 5, count: 20 },
    { id: uid(), amount: 2, count: 50 },
    { id: uid(), amount: 1, count: 100 },
  ],
});

/**
 * Initial game state
 */
const initialState: GameState = {
  // Game configuration
  dealName: "",
  ticketPrice: "",
  totalTickets: "",
  rows: getDefaultPayoutRows(),
  
  // Import/export state
  showImport: false,
  importText: "",
  importPreview: null,
  
  // UI state
  toast: null,
  inputValues: {},
  
  // History state
  history: [],
  historyIndex: -1,
  maxHistorySize: 20,
  
  // Meta state
  hasUnsavedChanges: false,
  lastSaved: null,
  autoSave: false,
};

/**
 * Game Management Zustand Store
 */
export const useGameStore = create<GameStore>()(
  persist(
    subscribeWithSelector(
      immer((set, get) => ({
        ...initialState,

        // Basic game configuration actions
        setDealName: (name: string) => {
          set((state) => {
            state.dealName = name;
            state.hasUnsavedChanges = true;
          });
        },

        setTicketPrice: (price: string) => {
          set((state) => {
            state.ticketPrice = price;
            state.hasUnsavedChanges = true;
          });
        },

        setTotalTickets: (total: string) => {
          set((state) => {
            state.totalTickets = total;
            state.hasUnsavedChanges = true;
          });
        },

        setRows: (rows: PayoutRow[]) => {
          set((state) => {
            state.rows = rows;
            state.hasUnsavedChanges = true;
          });
        },

        // Payout row management
        addPayoutRow: (amount = 1) => {
          set((state) => {
            const newRow: PayoutRow = {
              id: uid(),
              amount,
              count: 0,
            };
            state.rows.push(newRow);
            state.rows.sort((a, b) => b.amount - a.amount);
            state.hasUnsavedChanges = true;
          });
        },

        updatePayoutRow: (id: string, updates: Partial<PayoutRow>) => {
          set((state) => {
            const rowIndex = state.rows.findIndex(row => row.id === id);
            if (rowIndex !== -1) {
              Object.assign(state.rows[rowIndex], updates);
              state.hasUnsavedChanges = true;
            }
          });
        },

        removePayoutRow: (id: string) => {
          set((state) => {
            state.rows = state.rows.filter(row => row.id !== id);
            state.hasUnsavedChanges = true;
          });
        },

        duplicatePayoutRow: (id: string) => {
          set((state) => {
            const row = state.rows.find(r => r.id === id);
            if (row) {
              const newRow: PayoutRow = {
                ...row,
                id: uid(),
              };
              state.rows.push(newRow);
              state.rows.sort((a, b) => b.amount - a.amount);
              state.hasUnsavedChanges = true;
            }
          });
        },

        sortPayoutRows: () => {
          set((state) => {
            state.rows.sort((a, b) => b.amount - a.amount);
          });
        },

        // Import/export actions
        setShowImport: (show: boolean) => {
          set((state) => {
            state.showImport = show;
          });
        },

        setImportText: (text: string) => {
          set((state) => {
            state.importText = text;
          });
        },

        setImportPreview: (preview: PayoutRow[] | null) => {
          set((state) => {
            state.importPreview = preview;
          });
        },

        importRows: (rows: PayoutRow[]) => {
          set((state) => {
            state.rows = rows.map(row => ({ ...row, id: uid() }));
            state.rows.sort((a, b) => b.amount - a.amount);
            state.showImport = false;
            state.importText = "";
            state.importPreview = null;
            state.hasUnsavedChanges = true;
          });
        },

        exportGameConfig: (): GameConfig => {
          const state = get();
          return {
            dealName: state.dealName,
            ticketPrice: state.ticketPrice,
            totalTickets: state.totalTickets,
            rows: state.rows,
          };
        },

        // UI actions
        setToast: (message: string | null) => {
          set((state) => {
            state.toast = message;
          });
        },

        setInputValue: (key: string, value: string) => {
          set((state) => {
            state.inputValues[key] = value;
          });
        },

        clearInputValues: () => {
          set((state) => {
            state.inputValues = {};
          });
        },

        // History actions
        saveToHistory: () => {
          set((state) => {
            const currentConfig = {
              dealName: state.dealName,
              ticketPrice: state.ticketPrice,
              totalTickets: state.totalTickets,
              rows: [...state.rows],
            };

            // Remove any entries after current index (for when we're in middle of history)
            state.history = state.history.slice(0, state.historyIndex + 1);
            
            // Add new entry
            state.history.push(currentConfig);
            
            // Keep history size within limits
            if (state.history.length > state.maxHistorySize) {
              state.history = state.history.slice(-state.maxHistorySize);
            }
            
            state.historyIndex = state.history.length - 1;
          });
        },

        undo: () => {
          const state = get();
          if (state.historyIndex > 0) {
            const prevConfig = state.history[state.historyIndex - 1];
            set((draft) => {
              draft.dealName = prevConfig.dealName;
              draft.ticketPrice = prevConfig.ticketPrice;
              draft.totalTickets = prevConfig.totalTickets;
              draft.rows = [...prevConfig.rows];
              draft.historyIndex = state.historyIndex - 1;
              draft.hasUnsavedChanges = true;
            });
            return true;
          }
          return false;
        },

        redo: () => {
          const state = get();
          if (state.historyIndex < state.history.length - 1) {
            const nextConfig = state.history[state.historyIndex + 1];
            set((draft) => {
              draft.dealName = nextConfig.dealName;
              draft.ticketPrice = nextConfig.ticketPrice;
              draft.totalTickets = nextConfig.totalTickets;
              draft.rows = [...nextConfig.rows];
              draft.historyIndex = state.historyIndex + 1;
              draft.hasUnsavedChanges = true;
            });
            return true;
          }
          return false;
        },

        canUndo: () => {
          const state = get();
          return state.historyIndex > 0;
        },

        canRedo: () => {
          const state = get();
          return state.historyIndex < state.history.length - 1;
        },

        clearHistory: () => {
          set((state) => {
            state.history = [];
            state.historyIndex = -1;
          });
        },

        // Game lifecycle actions
        loadExampleGame: () => {
          const example = getExampleGame();
          set((state) => {
            Object.assign(state, example);
            state.hasUnsavedChanges = true;
          });
        },

        resetGame: () => {
          set((state) => {
            state.dealName = "";
            state.ticketPrice = "";
            state.totalTickets = "";
            state.rows = getDefaultPayoutRows();
            state.hasUnsavedChanges = false;
            state.lastSaved = null;
            state.toast = null;
          });
        },

        saveGame: () => {
          set((state) => {
            state.hasUnsavedChanges = false;
            state.lastSaved = Date.now();
          });
        },

        loadGame: (config: GameConfig) => {
          set((state) => {
            Object.assign(state, config);
            state.hasUnsavedChanges = false;
            state.lastSaved = Date.now();
          });
        },

        // Utility actions
        markSaved: () => {
          set((state) => {
            state.hasUnsavedChanges = false;
            state.lastSaved = Date.now();
          });
        },

        setAutoSave: (enabled: boolean) => {
          set((state) => {
            state.autoSave = enabled;
          });
        },
      }))
    ),
    {
      name: 'game-store',
      // Persist game configuration and UI state
      partialize: (state) => ({
        dealName: state.dealName,
        ticketPrice: state.ticketPrice,
        totalTickets: state.totalTickets,
        rows: state.rows,
        autoSave: state.autoSave,
      }),
    }
  )
);

/**
 * Game Store Selectors
 */
export const gameSelectors = {
  /** Select current game configuration */
  gameConfig: (state: GameStore): GameConfig => ({
    dealName: state.dealName,
    ticketPrice: state.ticketPrice,
    totalTickets: state.totalTickets,
    rows: state.rows,
  }),
  /** Select payout rows */
  payoutRows: (state: GameStore) => state.rows,
  /** Select if game has unsaved changes */
  hasUnsavedChanges: (state: GameStore) => state.hasUnsavedChanges,
  /** Select toast message */
  toast: (state: GameStore) => state.toast,
  /** Select import state */
  importState: (state: GameStore) => ({
    showImport: state.showImport,
    importText: state.importText,
    importPreview: state.importPreview,
  }),
  /** Select if undo is available */
  canUndo: (state: GameStore) => state.historyIndex > 0,
  /** Select if redo is available */
  canRedo: (state: GameStore) => state.historyIndex < state.history.length - 1,
};

/**
 * Convenience hooks for common game store operations
 */

/** Hook to get current payout rows */
export const usePayoutRows = () => useGameStore(gameSelectors.payoutRows);

/** Hook to get game configuration */
export const useGameConfig = () => useGameStore(gameSelectors.gameConfig);

/** Hook to check for unsaved changes */
export const useHasUnsavedChanges = () => useGameStore(gameSelectors.hasUnsavedChanges);

/** Hook to get toast notifications */
export const useGameToast = () => useGameStore(gameSelectors.toast);

/** Hook to get import state */
export const useImportState = () => useGameStore(gameSelectors.importState);